<script>
	import { mapGetters, mapState } from "vuex";
	import BillingDropdownBody from "./DropdownBody";
	import BaseStylableTextarea from "../UI/BaseStylableTextarea";
	import BaseTypeAhead from "../UI/BaseTypeAhead";
	import { query } from "sinao-corejs";
	import { htmlToMd, mdToHtml, debouncer } from '../../mixins/methods';

	export default {
		components: {
			BillingDropdownBody,
			BaseStylableTextarea,
			BaseTypeAhead
		},
		props: {
			dataAsProps: {
				type: Object,
				required: false,
				default: undefined
			},
			sectionId: {
				type: Number,
				required: true
			},
			index: {
				type: Number,
				required: true
			},
			lineNumber: {
				type: String,
				required: true
			}
		},
		data() {
			return {
				// initialise the default values else where to be able to retrieve it later
				...this.initializeData(),
				...this.dataAsProps,
				reformulateList: [],
				shouldAutofocus: false,
				loading: false,
				$_debounceTimer: null,
				productList: [],
				noMoreProductSuggestions: false,
				lastProductSearch: ""
			};
		},
		computed: {
		...mapGetters(["enabledHeaders", "vatsValues", "currency", "currencySpacing", "is_sap","isAvoid"]),
		...mapState({
			appId: state => state.app.id,
			selectedVatValue: state => state.vat.selectedVatValue,
			headers: state => state.billing.headers,
			isEditable: state => state.home.isEditable,
		}),
		unitSpacing() {
			return {
			"padding-right": `${this.currencySpacing * 6}px`
			};
		},
		calculate() {
			const {quantity, amount, amountWithTaxes, discount, vatPercentage, progress, enabledHeaders} = this;

			return {
			quantity,
			amount,
			amountWithTaxes,
			discount,
			vatPercentage,
			progress,
			enabledHeaders
			};
		},
		filteredProductList() {
			if (this.reformulateList.length > 0) {
			return this.reformulateList
			}

			/*
			// regexp definition
			const hashStart = /^\\#/
			// functions definition
			const cleanString = string => {
			if (!string) {
				return "";
			}

			return string
				.toLowerCase()
				.normalize("NFD")
				.replace(/[\u0300-\u036f]/g, "")
				.replace(/<[^>]*>?/gm, "")
				.replace(/[^A-Za-z0-9]/g, "");
			};
			const checkProductTags = function (product, userTags) {
			const productTags = product.tags

			return userTags.every(userTag => {
				userTag = userTag.trimEnd()
				return productTags.find(tag => cleanString(tag).search(cleanString(userTag)) !== -1)
					!== undefined
			})
			}
			const checkProductStrings = function (product, string) {
			const searchByName = cleanString(product?.name).search(cleanString(string)) !== -1;
			const searchByReference = cleanString(product?.reference).search(cleanString(string)) !== -1;
			const searchByEanCode = cleanString(product?.ean_code).search(cleanString(string)) !== -1;

			return searchByName || searchByReference || searchByEanCode || checkProductTags(product, string.split(' '));
			}
			//this.designation start with a #
			if (hashStart.test(this.designation)) {
			const userTags = this.designation.split(/#/).filter(tag => cleanString(tag).length > 0)
			return this.productList.filter(product => checkProductTags(product, userTags))
			}
			// is not starting with a #
			let tlDestination = null;
			if ((this.designation?.length && this.reference?.length) || (!this.designation?.length && this.reference?.length)) {
			tlDestination = this.reference;
			} else if (this.designation?.length && !this.reference?.length) {
			tlDestination = this.designation;
			}

			return !tlDestination ? [] : this.productList.filter(product => checkProductStrings(product, tlDestination))
			*/

			// Retourner la liste déjà filtrée par handleAutocompleteProduct (pas besoin de re-filtrer, ça crée des résultats vides)
			return this.productList;

		},
		// watchable unique value representing all fields needed to evaluate allowdelete
		fieldsWatchable() {
			return this.designation + this.convertNumberToString(this.totalExclTax);
		}
		},
		watch: {
			reference(newValue, oldValue) {
				if (newValue !== oldValue) {
					if (this.$_debounceTimer) {
						clearTimeout(this.$_debounceTimer);
					}
					this.$_debounceTimer = debouncer(() => {
						this.handleAutocompleteProduct(newValue, oldValue, {
						isDesignationActive: false
						});
					}, 300);
				}
			},
			designation(newValue, oldValue) {
				if (newValue !== oldValue) {
					if (this.$_debounceTimer) {
						clearTimeout(this.$_debounceTimer);
					}
					this.$_debounceTimer = debouncer(() => {
						this.handleAutocompleteProduct(newValue, oldValue, {
						isDesignationActive: true
						});
					}, 300);
				}
			},
			calculate: {
				handler() {
					this.calculateTotalWithoutTax();
					this.calculateAmountWithTaxes();
					this.calculateTotalIncludingTax();
					this.calculateVatAmount();
				},
				deep: true,
				immediate: true
			},
			taxes(newValue, oldValue) {
				this.cacheOfVatPercentage = oldValue;
			},
			selectedVatValue(newValue, oldValue) {
				// The VAT is "Exoneration de TVA" so we cache the actual vatPercentage
				if (newValue === 0) {
					this.cacheOfVatPercentage = this.vatPercentage;
				}

				// The old VAT is "Exoneration de TVA" and the new VAT is "TVA multiple" so we restore the cached vatPercentage
				if (oldValue === 0 && newValue === null) {
					this.taxes = this.cacheOfVatPercentage;
				}

				// Ignore "TVA multiple"
				if (newValue !== null) {
					this.vatPercentage = this.convertNumberToString(newValue);
				}
			},
			vatPercentage() {
				if (this.amountWithTaxesDriven) {
					this.amount = this.calculateAmountWithOutTaxes();
				}
			},
			amountWithTaxes(newValue) {
				if (newValue && newValue !== "0,00" && this.amountWithTaxesDriven) {
					this.amount = this.calculateAmountWithOutTaxes();
				}
			},
			$data: {
				handler(newValue) {
					this.$emit("update:dataAsProps"	, newValue);
				},
				deep: true,
				immediate: true
			},
			fieldsWatchable(newValue) {
				const initData = this.initializeData();
				const comparableInitData = initData.designation + initData.totalExclTax;

				if (newValue == comparableInitData) {
					this.allowDelete++;
				} else {
					this.allowDelete = 0;
				}
			}
			},
			created() {
				// if amountWithTaxes is stored in database => amountWithTaxesDriven must be true
				// comparing 0.00 on both FR & US format.
				if (this.dataAsProps?.amountWithTaxes && (this.dataAsProps?.amountWithTaxes !== "0,00" && this.dataAsProps?.amountWithTaxes !== "0.00")) {
					this.amountWithTaxesDriven = true;
					this.amountWithTaxes = this.convertStringToNumber((this.dataAsProps?.amountWithTaxes ?? 0));
				}
				// If there is data just format it
				if (this.dataAsProps) {
					this.vatPercentage = this.convertNumberToString(this.dataAsProps.vatPercentage);
				} else {
					// If there is not dat, load the selectedVatValue, if not available switch to the default 20,00%
					this.vatPercentage =
						this.convertNumberToString(this.selectedVatValue) ?? this.convertNumberToString(20);
				}

				if (this.convertStringToNumber(this.discount?.amount) > 0) {
					this.discount.value = this.discount.amount;
				}

				if (this.convertStringToNumber(this.discount?.percent) > 0) {
					this.discount.value = `${this.discount.percent}%`;
				}
			},
		methods: {
			async handleAutocompleteProduct(newValue, oldValue, { isDesignationActive }) {
				// if (isDesignationActive && this.reference?.length) return; // we search by reference if not empty
				if (!isDesignationActive && !this.reference?.length) return;
				if (isDesignationActive && !newValue?.length) return;

				// Si même valeur → stop
				//if (newValue === oldValue) return;

				const mdNewValue = htmlToMd(newValue);

				// Si une recherche précédente pour un préfixe identique a déjà renvoyé zéro résultat → stop
				if (
					this.noMoreProductSuggestions &&
					mdNewValue?.startsWith(this.lastProductSearch)
				) {
				return;
				}
				
				this.lastProductSearch = mdNewValue;
				const results = await this.$store.dispatch("products", { searchValue: mdNewValue });
				if (Array.isArray(results)) { // results can be undefined when the request aborts
					this.productList = results;
					// Si aucun résultat → on bloque les prochaines recherches tant que la saisie affine
					this.noMoreProductSuggestions = results.length === 0;
					// Si on retape plus court → on réactive les recherches
					if (!mdNewValue?.startsWith(this.lastProductSearch)) {
					this.noMoreProductSuggestions = false;
					}
				}
			},
			displayTwoDecimal (value) {
				return this.convertNumberToString(value, 2);
			},
			remove({ sectionId, index }) {
				this.$emit("event-remove", { sectionId, index });
			},
			duplicate(position) {
				this.$emit("event-append", {
					component: "entry",
					data: this.$data,
					sectionId: this.sectionId,
					position
				});
			},
			append({ component, position }) {
				this.$emit("event-append", {
					component,
					sectionId: this.sectionId,
					position
				});
			},
			async reformulateWithAI(position) {
				this.loading = true;
				let description = this.designation.replace(/<br>|\*\*|\*/g, "");

				await query({
					operationId: "app.openai.billings.reformulate",
					params: {
						appId: this.appId,
						description: ' " '+description+' " '
					},
					data: { }
				}).then((response) => {
					this.loading = false;
					this.reformulateList = response;

					const childComponent = this.$refs.inputDesignation;
					childComponent.toggleEditor();

					const watcher = this.$watch('designation', (newValue, oldValue) => {
						if (newValue !== oldValue) {
							this.reformulateList = [];
						}
					});

					this.$once('hook:beforeDestroy', () => {
						watcher();
					});
				}).error((error) => {
					this.loading = false;
				});
			},
			async smartCorrector(position) {
				this.loading = true;
				let description = this.designation.replace(/<br>|\*\*|\*/g, "");

				await query({
					operationId: "app.openai.billings.smartCorrector",
					params: {
						appId: this.appId,
						description: ' " '+description+' " '
					},
					data: { }
				}).then((response) => {
					this.designation = response;
					this.loading = false;
				}).error((error) => {
					this.loading = false;
				});
			},
			calculateTotalWithoutTax() {
				const _progress = this.convertStringToNumber(this.progress);

				const _quantity = this.convertStringToNumber(this.quantity);

				const _amount = this.convertStringToNumber(this.amount);

				const _discountAmount = this.convertStringToNumber(this.discount.amount);

				const _discountPercentage = this.convertStringToNumber(this.discount.percent);

				let revenue = _quantity * _amount;

				if (this.enabledHeaders.progress) {
					if (_progress > 0) {
						revenue = revenue * (_progress / 100);
					} else {
						revenue = 0;
					}
				} 

				const discount = _discountAmount || (_discountPercentage / 100) * revenue;

				this.totalExclTax = this.convertNumberToString(revenue - discount, 2);

				return this.totalExclTax;
			},
			
			calculateAmountWithTaxes() {
				if (!this.amountWithTaxesDriven) {
					const _amount = this.convertStringToNumber(this.amount);
					const _vatPercentage = this.convertStringToNumber(this.vatPercentage);
					//Prix TTC (en €) = Prix HT (en €) x (1 + Taux TVA (en %) )
					const _amountWithTaxes = _amount * (1 + (_vatPercentage / 100));
					this.amountWithTaxes = this.convertNumberToString(_amountWithTaxes);
				}
			},
			calculateAmountWithOutTaxes() {
				const _amountWithTaxes = this.convertStringToNumber(this.amountWithTaxes);
				const _vatPercentage = this.convertStringToNumber(this.vatPercentage);
				const _amount = this.convertNumberToString(_amountWithTaxes / (1 + _vatPercentage / 100), 5);

				return this.amount = _amount ;
			},
			calculateVatAmount() {
				const _totalIcludingTax = this.convertStringToNumber(this.totalInclTax);
				const _totalExclTax = this.convertStringToNumber(this.totalExclTax);

				this.vatAmount = this.convertNumberToString(
					_totalIcludingTax - _totalExclTax,
					5
				);

				return this.vatAmount;
			},
			calculateTotalIncludingTax() {
				let _totalInclTax = 0;

				const _progress = this.convertStringToNumber(this.progress);
				
				const _discountAmount = this.convertStringToNumber(this.discount.amount);

				const _discountPercentage = this.convertStringToNumber(this.discount.percent);

				const _quantity = this.convertStringToNumber(this.quantity);

				const _amount = this.convertStringToNumber(this.amount);

				const _vatPercentage =  this.convertStringToNumber(this.vatPercentage)

				let revenue = _quantity * _amount;

				if (this.enabledHeaders.progress) {
					if (_progress > 0) {
						revenue = revenue * (_progress / 100);
					} else {
						revenue = 0;
					}
				} 

				const discount = _discountAmount || (_discountPercentage / 100) * revenue;
				
				if (this.amountWithTaxesDriven) {
					// must check progress
					_totalInclTax =  this.convertStringToNumber(this.amountWithTaxes)  *  this.convertStringToNumber(this.quantity);
					if (this.enabledHeaders.progress) {
						const _progress = this.convertStringToNumber(this.progress);
						 _totalInclTax = _totalInclTax * (_progress / 100);
					} 
					// must check discount
					// $due = round($due) - round($this->discount_value * (1+($this->vat_percent / 10000)));				
					_totalInclTax = this.convertNumberToString((_totalInclTax - (discount * (1 + (_vatPercentage / 100)))));
				} else {
					// ($subtotal + ($subtotal * ($this->vat_percent / 10000)))
					const _totalExclTax = this.convertStringToNumber(this.totalExclTax);
					const _vatPercentage = this.convertStringToNumber(this.vatPercentage);
					_totalInclTax = _totalExclTax + (_totalExclTax * (_vatPercentage / 100));
          _totalInclTax = (Math.round(_totalInclTax * 100000) / 100000);  // make a round
				}
				
        this.totalInclTax = _totalInclTax;
				return this.totalInclTax;
			},
			formatDiscount() {
				const discount = this.convertStringToNumber(this.discount.value);

				if (typeof this.discount.value === "string" && this.discount.value.includes("%")) {
					this.discount.amount = null;
					this.discount.percent = discount;
					this.discount.value = `${discount}%`;
				} else {
					this.discount.percent = null;
					this.discount.amount = discount;
					this.discount.value = this.convertNumberToString(discount);
				}

				return this.discount.value;
			},
			selectProduct(item) {
				if(this.reformulateList.length > 0) {
					this.reformulateList = [];
					this.designation = mdToHtml(item);
					return;
				}

				this.product = {
					id: item.id,
					type: item.type,
					category: item.category ?? null
				};

				if (item.account_id != null && item.account_id != undefined)
					this.accountId = item.account_id;

				this.designation = mdToHtml(item.name);
				this.designation += (item.reference  && !this.enabledHeaders.reference) ? "  \n" + mdToHtml(item.reference) : "";
				this.designation += item.description
					? "  \n" + mdToHtml(item.description)
					: "";

				this.reference = mdToHtml(item.reference ?? " ");
				this.amount = this.convertNumberToString(item.amount_accurately / 1000 / 100);				
				this.vatPercentage = this.convertNumberToString(item.vat_percent / 100);
				this.unity = item.quantity_name;

				if (item.quantity_name && item.quantity_name !== "unité") {
					this.$store.commit("enableHeaderByKey", "quantity_name");
				}

				if (this.is_sap) {
					this.$emit("sapSortContent", item);
				}
			},
			// initialising data here to keep the default values in on place
			initializeData() {
				return {
					tabIndex: 0,
          			reference: "",
					designation: "",
					quantity: this.convertNumberToString(1, 2),
					unity: "",
					amount: this.convertNumberToString(0, 2),
					amountWithTaxes: null,
					totalQuantity: "0,00",
					progress: "0,00",
					discount: {
						value: this.convertNumberToString(0, 2),
						amount: 0,
						percent: 0
					},
					totalExclTax: this.convertNumberToString(0, 7),
					vatPercentage: null,
					vatAmount: this.convertNumberToString(0, 7),
					cacheOfVatPercentage: null,
					totalInclTax: this.convertNumberToString(0, 7),
					allowDelete: 0,
					product: {},
					accountId: null,
					amountWithTaxesDriven: false
				};
			}
		}
	};
</script>

<template>
	<Fragment>
		<b-td v-if="enabledHeaders.line_number" class="billing__cell line_number_cell" :style="{'width': headers.line_number.width}">
			<BillingDropdownBody
				v-if="enabledHeaders.line_number"
				:section-id="sectionId"
				:index="index"
				:account-id.sync="accountId"
				style="top: 0; left: 35px; position: absolute;"
				@event-append="append"
				@event-duplicate="duplicate"
				@event-remove="remove"
				@event-reformulateWithAI="reformulateWithAI"
				@event-smartCorrector="smartCorrector"
			/>
			
			<div
				class="line-number"
				:style="{'width': headers.line_number.width}"
			>
				{{ lineNumber }}
			</div>
		</b-td>
    <b-td v-if="enabledHeaders.reference" class="billing__cell small_cell">
      <BillingDropdownBody
          v-if="!enabledHeaders.line_number && isEditable"
          :section-id="sectionId"
          :index="index"
          :account-id.sync="accountId"
          style="top: 0; left: 349px; position: absolute"
          @event-append="append"
          @event-duplicate="duplicate"
          @event-remove="remove"
          @event-reformulateWithAI="reformulateWithAI"
          @event-smartCorrector="smartCorrector"
      />
      <BaseTypeAhead
          v-model.lazy="reference"
          :list="filteredProductList"
          :field="'reference'"
          :display-no-result="false"
		  class="reference"
          @select-item="selectProduct"
      >
        <template #input>
          <BaseStylableTextarea
              ref="inputReference"
              v-model.lazy="reference"
              :autofocus="appId === 0 ? false : true"
              :placeholder="$t('Référence')"
              class="billing__input billing__input--fullWidth"
          />

          <b-overlay
              :show="loading"
              rounded
              opacity="0.6"
              spinner-small
              spinner-variant="primary"
              style="top: -25px !important;"
          />
        </template>

        <template #suggestion="suggestion">
          <div
              v-if="suggestion.item.reference != null"
              class="suggestion"
          >
						<span class="suggestion_description">
							<span class="suggestion_name">
								{{ suggestion.item.reference ? `${suggestion.item.reference}` : "" }}
							</span>
						</span>
          </div>

          <div
              v-else-if="suggestion.item.length > 0"
              class="suggestion"
          >
						<span class="suggestion_description " style="padding: 4px 10px !important; display: flex; align-items: center;">
							<b-icon-stars class="suggestion_icon" style="color: #a183bf;" font-scale=“1.2” />

							<span class="suggestion_name">
								{{ suggestion.item }}
							</span>
						</span>
          </div>
        </template>
      </BaseTypeAhead>
    </b-td>
		<b-td class="billing__cell billing__cell-designation" tabindex="1">
			<BillingDropdownBody
				v-if="!enabledHeaders.line_number && !enabledHeaders.reference && isEditable"
				:section-id="sectionId"
				:index="index"
				:account-id.sync="accountId"
				style="top: 0; left: 349px; position: absolute"
				@event-append="append"
				@event-duplicate="duplicate"
				@event-remove="remove"
				@event-reformulateWithAI="reformulateWithAI"
				@event-smartCorrector="smartCorrector"
			/>



			<BaseTypeAhead
				v-model.lazy="designation"
				:list="filteredProductList"
				:field="'name'"
				:display-no-result="false"
				@select-item="selectProduct"
			>
				<template #input>
					<BaseStylableTextarea
						ref="inputDesignation"
						v-model.lazy="designation"
						:autofocus="appId === 0 ? false : true"
						:placeholder="$t('Produit ou service')"
						class="billing__input billing__input--fullWidth"
					/>

					<b-overlay
						:show="loading"
						rounded
						opacity="0.6"
						spinner-small
						spinner-variant="primary"
						style="top: -25px !important;"
					/>
				</template>

				<template #suggestion="suggestion">
					<div
						v-if="suggestion.item.name != null"
						class="suggestion"
					>
						<span class="suggestion_description">
							<img
								class="suggestion_icon"
								:src="suggestion.item.image || 'https://assets.sinao.app/resources/images/product.png'"
								alt="icon"
							>
							<span class="suggestion_name">
								{{ suggestion.item.name }}
								<span style="color: grey">
									{{ suggestion.item.reference ? `- ${suggestion.item.reference}` : "" }}
									{{ suggestion.item.ean_code ? `- ${suggestion.item.ean_code}` : "" }}
								</span>
							</span>
						</span>
						<span class="suggestion_amount" style="padding-left: 4px;">
							{{ convertNumberToString(suggestion.item.amount_accurately / 1000 / 100) }}
							{{ currency }}
						</span>
					</div>

					<div
						v-else-if="suggestion.item.length > 0"
						class="suggestion"
					>
						<span class="suggestion_description " style="padding: 4px 10px !important; display: flex; align-items: center;">
							<b-icon-stars class="suggestion_icon" style="color: #a183bf;" font-scale=“1.2” />

							<span class="suggestion_name">
								{{ suggestion.item }}
							</span>
						</span>
					</div>
				</template>
			</BaseTypeAhead>
		</b-td>
		<b-td v-if="enabledHeaders.quantity" class="billing__cell small_cell">
			<b-form-input
				v-model.lazy="quantity"
				autocomplete="off"
				lazy-formatter
				:formatter="convertNumberToString"
				placeholder="Ex: 2"
				class="billing__input text-right"
				@focus="$event.target.select()"
			/>
		</b-td>
		<b-td v-if="enabledHeaders.quantity_name" class="billing__cell small_cell">
			<b-form-input
				v-model.lazy="unity"
				autocomplete="off"
				placeholder="U"
				class="billing__input text-right"
				@focus="$event.target.select()"
			/>
		</b-td>
		<b-td v-if="enabledHeaders.info_total_quantity" class="billing__cell small_cell">
			<b-form-input
				v-model.lazy="totalQuantity"
				autocomplete="off"
				lazy-formatter
				:formatter="convertNumberToString"
				placeholder="100"
				class="billing__input text-right"
				@focus="$event.target.select()"
			/>
		</b-td>
		<b-td v-if="enabledHeaders.amount" class="billing__cell unit" :data-unit="currency" :style="unitSpacing">
			<b-form-input
				v-model.lazy="amount"
				autocomplete="off"
				lazy-formatter
				:formatter="convertNumberToString"
				placeholder="Ex: 10"
				class="billing__input text-right"
				@focus="$event.target.select()"
				@update="amountWithTaxesDriven = false"
			/>
		</b-td>
		<b-td v-if="enabledHeaders.progress" class="billing__cell unit small_cell" data-unit="%">
			<b-form-input
				v-model.lazy="progress"
				autocomplete="off"
				placeholder="Ex: 10"
				class="billing__input text-right"
				@focus="$event.target.select()"
				@blur="progress = convertNumberToString(progress, 2)"
			/>
		</b-td>
		<b-td
			v-if="enabledHeaders.amount_with_taxes"
			class="billing__cell unit"
			:data-unit="currency"
			:style="unitSpacing"
		>
			<b-form-input
				v-model.lazy="amountWithTaxes"
				autocomplete="off"
				lazy-formatter
				:formatter="convertNumberToString"
				placeholder="Ex: 10"
				class="billing__input text-right"
				@focus="$event.target.select()"
				@update="amountWithTaxesDriven = true"
			/>
		</b-td>
		<b-td v-if="enabledHeaders.discount" class="billing__cell unit">
			<b-form-input
				v-model.lazy="discount.value"
				autocomplete="off"
				lazy-formatter
				:formatter="formatDiscount"
				:placeholder="`${currency} ou %`"
				class="billing__input text-right"
				@focus="$event.target.select()"
			/>
		</b-td>
		<b-td
			v-if="enabledHeaders.subtotal"
			class="billing__cell billing__cell-readonly unit"
			:data-unit="currency"
			:style="unitSpacing"
		>
			<b-form-input
				:value="totalExclTax | convertNumberToString(2)"
				tabindex="-1"
				readonly="readonly"
				type="text"
				placeholder="0,00"
				class="billing__input text-right"
				@focus="$event.target.select()"
			/>
		</b-td>
		<b-td
			v-if="enabledHeaders.due"
			class="billing__cell billing__cell-readonly unit"
			:data-unit="currency"
			:style="unitSpacing"
		>
			<b-form-input
				:value="totalInclTax | convertNumberToString(2)"
				tabindex="-1"
				readonly="readonly"
				placeholder="0,00"
				type="text"
				class="billing__input text-right"
				@focus="$event.target.select()"
			/>
		</b-td>
		<b-td v-if="enabledHeaders.vat_percent" class="billing__cell unit small_cell" data-unit="%">
			<b-form-input
				v-model.lazy="vatPercentage"
				autocomplete="off"
				lazy-formatter
				:formatter="convertNumberToString"
				placeholder="20"
				type="text"
				:class="['billing__input', 'text-right', { 'text-muted': isAvoid }]"
				@focus="$event.target.select()"
				:disabled="isAvoid"
				:style="{ backgroundColor: isAvoid ? 'transparent' : '' }"
			/>
		</b-td>
	</Fragment>
</template>

<style scoped lang="scss">
	@import "../../scss/mixins.scss";

	table td {
		border: none;
	}

	.suggestion {
		display: flex;
		justify-content: space-between;
		padding: 0.25em 0.5em;
		cursor: pointer;
		@media screen and (prefers-reduced-motion: no-preference) {
			transition: background-color 100ms ease-in-out;
		}

		&:hover {
			background-color: var(--background);
		}

		&_icon {
			border-radius: 50%;
			margin-right: 0.5em;
			width: 24px;
			height: 24px;
		}
	}
</style>
